import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useWebviewMode } from '../../hooks/useWebviewMode';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import ReactSelect from 'react-select';
import { useUser } from '@clerk/clerk-react';
import {
  Download,
  Star,
  ArrowUp,
  Send,
  MapPin,
  Search,
  Globe,
  Share2,
  Calendar as CalendarIcon,
  FileText,
  MessageSquare,
  Image as ImageIcon,
  TrendingUp,
  RefreshCw,
  Info,
  Eye
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { authSelector } from '../../store/slices/auth';
import {
  selectedClientSelector,
  setSelectedClient
} from '../../store/slices/selectedClient';
import { clientsSelector, getClients } from '../../store/slices/clients';
import {
  API_CLIENTS_NAME,
  API_KEY,
  METRICS_SUMMARY_ENDPOINT,
  GBP_PERFORMANCE_ENDPOINT,
  SEO_AGENTS_API
} from '../../utils/constants';
import { getGoogleProfileBeforeSnapshot } from '../../components/GoogleBusinessProfile/services/index';
import { accountManagers } from '../../utils/helpers';
import { red } from '@material-ui/core/colors';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const DATE_RANGE_OPTIONS = [
  { value: 'last-30-days', label: 'Last 30 Days' },
  { value: 'last-3-months', label: 'Last 3 Months' },
  { value: 'last-6-months', label: 'Last 6 Months' },
  { value: 'last-year', label: 'Last Year' }
];

const GBP_PERFORMANCE_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'calls', label: 'Calls' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'direction-requests', label: 'Directions' },
  { id: 'website-clicks', label: 'Website clicks' }
];

const EMPTY_GBP_SERIES = {
  total: 0,
  label: '',
  data: []
};

const EMPTY_GBP_PERFORMANCE = {
  overview: { ...EMPTY_GBP_SERIES, label: 'Business Profile interactions' },
  calls: { ...EMPTY_GBP_SERIES, label: 'Calls' },
  bookings: { ...EMPTY_GBP_SERIES, label: 'Bookings' },
  'direction-requests': { ...EMPTY_GBP_SERIES, label: 'Directions' },
  'website-clicks': { ...EMPTY_GBP_SERIES, label: 'Website clicks' }
};

const DATE_RANGE_TO_MONTHS = {
  'last-30-days': 1,
  'last-3-months': 3,
  'last-6-months': 6,
  'last-year': 12
};

const getMonthsFromRange = (range) => DATE_RANGE_TO_MONTHS[range] || 6;

const formatResponseRate = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '0%';
  }

  if (numericValue <= 1) {
    return `${Math.round(numericValue * 100)}%`;
  }

  return `${Math.round(numericValue)}%`;
};

const formatScoreChange = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  return numericValue > 0 ? `${numericValue}` : `${numericValue}`;
};

const toDisplayDate = (value) => {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const sectionCardStyle = {
  borderRadius: '1rem',
  border: 'none',
  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
};

const CircularScore = ({ value, suffix = '%' }) => {
  const numericValue = Number(value) || 0;
  const cappedValue = Math.min(100, Math.max(0, numericValue));
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (cappedValue / 100) * circumference;

  return (
    <div className="relative h-[132px] w-[132px] shrink-0">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="9"
        />
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke="#3766a8"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[30px] font-bold text-foreground leading-none">
          {numericValue}
          {suffix}
        </span>
      </div>
    </div>
  );
};

const dateRangeSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '56px',
    borderRadius: '0.75rem',
    borderColor: state.isFocused ? 'hsl(var(--primary))' : 'hsl(var(--border))',
    boxShadow: state.isFocused ? '0 0 0 1px hsl(var(--primary))' : 'none',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    paddingLeft: '0.25rem',
    paddingRight: '0.25rem',
    '&:hover': {
      borderColor: 'hsl(var(--primary))'
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: '0.5rem',
    paddingRight: '0.25rem'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#111827',
    fontSize: '1rem'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#94a3b8',
    paddingRight: '0.75rem',
    '&:hover': {
      color: '#94a3b8'
    }
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.75rem',
    overflow: 'hidden',
    border: '1px solid hsl(var(--border))',
    boxShadow:
      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    marginTop: '0.5rem'
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    overflowX: 'hidden'
  }),
  option: (provided, state) => ({
    ...provided,
    margin: 0,
    borderRadius: '0.375rem',
    paddingLeft: '0.875rem',
    paddingRight: '0.875rem',
    backgroundColor: state.isSelected
      ? 'hsl(var(--accent))'
      : state.isFocused
      ? 'hsl(var(--accent))'
      : 'transparent',
    color: '#111827',
    cursor: 'pointer'
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999
  })
};

const Stat = ({ value, label, accent = 'default', icon: Icon }) => {
  const colorMap = {
    default: '#111827',
    primary: '#3766a8',
    green: '#059669',
    yellow: '#d97706',
    red: '#ef4444'
  };

  return (
    <div className="space-y-1">
      <div
        className="flex items-center gap-2 text-3xl font-bold"
        style={{ color: colorMap[accent] || colorMap.default }}>
        {Icon && <Icon className="h-6 w-6" />}
        <span>{value}</span>
      </div>
      <p className="text-sm text-muted-foreground font-medium">{label}</p>
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, children, disabled = false }) => (
  <Card
    style={{
      ...sectionCardStyle,
      opacity: disabled ? 0.5 : 1,
      backgroundColor: disabled ? '#f5f5f5' : 'transparent',
      pointerEvents: disabled ? 'none' : 'auto'
    }}>
    <div className="px-5 sm:px-7 pt-5 pb-3">
      <div className="flex items-center gap-2.5">
        <div
          className={`h-9 w-9 rounded-xl flex items-center justify-center ${
            disabled ? 'bg-gray-200' : 'bg-primary/10'
          }`}>
          <Icon
            className={`h-5 w-5 ${disabled ? 'text-gray-400' : 'text-primary'}`}
          />
        </div>
        <h2
          className={`text-lg sm:text-2xl font-semibold ${
            disabled ? 'text-gray-400' : 'text-foreground'
          }`}>
          {title}
        </h2>
      </div>
    </div>
    <div className="px-5 sm:px-7 pb-6">{children}</div>
  </Card>
);

const Reports = () => {
  const reportContentRef = useRef(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useUser();
  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const { userInfo } = useSelector(authSelector);
  const isWebviewMode = useWebviewMode();

  const [dateRange, setDateRange] = useState('last-6-months');
  const [metricsSummary, setMetricsSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    () => window.innerWidth < 640
  );
  const [seoScanData, setSeoScanData] = useState(null);
  const [isLoadingSeo, setIsLoadingSeo] = useState(false);
  const [gbpSnapshotData, setGbpSnapshotData] = useState(null);
  const [gbpTab, setGbpTab] = useState('overview');
  const [gbpPerformanceData, setGbpPerformanceData] = useState(null);
  const [isLoadingGbpPerformance, setIsLoadingGbpPerformance] = useState(false);
  const [visibleSearchTerms, setVisibleSearchTerms] = useState(5);

  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!customerList?.length && userInfo?.accessToken) {
      dispatch(getClients());
    }
  }, [dispatch, customerList?.length, userInfo?.accessToken]);

  useEffect(() => {
    const clientId = selectedClient?.id;

    if (!clientId) {
      setMetricsSummary(null);
      return;
    }

    const fetchSummary = async () => {
      setIsLoadingSummary(true);

      try {
        const response = await axios.post(
          METRICS_SUMMARY_ENDPOINT,
          {
            clientId,
            months: getMonthsFromRange(dateRange)
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        setMetricsSummary(response?.data || null);
      } catch (error) {
        console.error('Error fetching metrics summary:', error);
        setMetricsSummary(null);
      } finally {
        setIsLoadingSummary(false);
      }
    };

    fetchSummary();
  }, [selectedClient?.id, dateRange]);

  useEffect(() => {
    const clientId = selectedClient?.id;

    if (!clientId || !GBP_PERFORMANCE_ENDPOINT) {
      setGbpPerformanceData(null);
      return;
    }

    const fetchGbpPerformance = async () => {
      setIsLoadingGbpPerformance(true);
      setVisibleSearchTerms(5);

      try {
        const response = await axios.post(
          GBP_PERFORMANCE_ENDPOINT,
          {
            clientId,
            months: getMonthsFromRange(dateRange)
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        setGbpPerformanceData(response?.data || null);
      } catch (error) {
        console.error('Error fetching GBP performance:', error);
        setGbpPerformanceData(null);
      } finally {
        setIsLoadingGbpPerformance(false);
      }
    };

    fetchGbpPerformance();
  }, [selectedClient?.id, dateRange]);

  const getCustomerList = useMemo(() => {
    if (!customerList?.length) {
      return [];
    }

    return customerList.map((customer) => ({
      ...customer,
      label: customer.clientName,
      value: customer.clientName
    }));
  }, [customerList]);

  // Reset snapshot when client changes
  useEffect(() => {
    setGbpSnapshotData(null);
  }, [selectedClient?.id]);

  // Fetch GBP snapshot for authoritative optimization percentage
  useEffect(() => {
    if (!selectedClient?.id) return;

    const fetchSnapshot = async () => {
      const snapshot = await getGoogleProfileBeforeSnapshot(selectedClient.id);
      setGbpSnapshotData(snapshot);
    };

    fetchSnapshot();
  }, [selectedClient?.id]);

  const reportsData = useMemo(() => {
    const metrics = metricsSummary?.metrics;
    const reviews = metrics?.reviews;
    const gbp = metrics?.googleBusinessProfile;
    const seo = metrics?.seo;
    const ranking = metrics?.localRanking;
    const social = metrics?.social;
    const accountOwnerId = selectedClient?.accountOwner;
    const accountManagerData = accountManagers.find(
      (manager) => manager.id === String(accountOwnerId)
    );

    return {
      reviewsWins: {
        newReviews: reviews?.newReviews ?? 0,
        averageRating: reviews?.averageRating ?? 0,
        fiveStarReviews: reviews?.fiveStarReviews ?? 0,
        respondedReviews: reviews?.reviewsResponded ?? 0,
        responseRate: formatResponseRate(reviews?.responseRate),
        smsRequests: reviews?.smsRequestsSent ?? 0,
        highlightedReview: reviews?.lastReviewMessage || 'No reviews yet',
        highlightedAuthor: 'Metrics summary'
      },
      profileHealth: {
        optimization: (gbpSnapshotData?.afterPercentage != null
          ? Math.round(gbpSnapshotData.afterPercentage)
          : Number(gbp?.optimizationScore || 0)
        ).toFixed(0),
        gbpPosts: gbp?.postsPublished ?? 0,
        photosAdded: gbp?.photosAdded ?? 0
      },
      seoScan: {
        score: seoScanData?.overallScore ?? seo?.score ?? 0,
        passed: seoScanData?.summary?.passed ?? seo?.passed ?? 0,
        warnings: seoScanData?.summary?.warnings ?? seo?.warnings ?? 0,
        failed: seoScanData?.summary?.failed ?? seo?.failed ?? 0,
        lastScan: seoScanData?.completedAt
          ? toDisplayDate(seoScanData.completedAt)
          : seo?.lastScanDate
          ? toDisplayDate(seo.lastScanDate)
          : '-'
      },
      rankingMomentum: {
        keywordsTracked: ranking?.keywordsTracked ?? 0,
        rankedTop3: ranking?.top3Ranked ?? 0,
        improvedPeriod: ranking?.improvedKeywords ?? 0,
        keywords: (ranking?.topKeywords || []).map((item) => ({
          keyword: item.keyword,
          before: item.previousRank,
          after: item.currentRank
        }))
      },
      socialContent: {
        postsPublished: social?.postsPublished ?? 0,
        upcomingPosts: social?.scheduledPosts ?? 0
      },
      accountManager: {
        name: accountManagerData?.full_name || '-',
        email: accountManagerData?.email || ''
      }
    };
  }, [
    metricsSummary,
    selectedClient?.accountOwner,
    seoScanData,
    gbpSnapshotData
  ]);

  const activeGbpSeries = useMemo(() => {
    const performance =
      gbpPerformanceData?.performance || EMPTY_GBP_PERFORMANCE;
    const series =
      performance[gbpTab] ||
      EMPTY_GBP_PERFORMANCE[gbpTab] ||
      EMPTY_GBP_PERFORMANCE.overview;
    const tabLabel = GBP_PERFORMANCE_TABS.find(
      (tab) => tab.id === gbpTab
    )?.label;

    if (tabLabel && gbpTab !== 'overview') {
      return { ...series, label: tabLabel };
    }

    return series;
  }, [gbpPerformanceData, gbpTab]);

  const discoveryBreakdown = gbpPerformanceData?.discovery?.breakdown || [];
  const discoveryTotalViews = gbpPerformanceData?.discovery?.totalViews ?? 0;
  const searchKeywordTerms = gbpPerformanceData?.searchKeywords?.terms || [];
  const searchKeywordsTotal =
    gbpPerformanceData?.searchKeywords?.totalLabel ?? '0';
  const searchKeywordsRangeLabel =
    gbpPerformanceData?.searchKeywords?.rangeLabel || null;
  const visibleKeywords = searchKeywordTerms.slice(0, visibleSearchTerms);

  const handleSeoRefresh = async () => {
    const clientId = selectedClient?.id;
    const url = selectedClient?.mergeFields?.['~Website'];

    if (!clientId || !url) return;

    setIsLoadingSeo(true);
    try {
      // Step 1: Start the audit
      const auditStartResponse = await axios.post(
        `https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev/audit`,
        {
          url,
          maxPages: 50,
          version: 'v3',
          forceRescan: true,
          lookupOnly: false
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { reportId } = auditStartResponse?.data || {};

      if (!reportId) {
        throw new Error('No reportId received from audit start');
      }

      // Step 2: Poll for completion
      let auditData = null;
      let attempts = 0;
      const maxAttempts = 60; // Max 5 minutes (5 seconds * 60)

      while (attempts < maxAttempts) {
        const statusResponse = await axios.get(
          `https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev/audit-v2/job/${reportId}`,
          { headers: { 'Content-Type': 'application/json' } }
        );

        const statusData = statusResponse?.data;

        if (statusData?.status === 'completed') {
          auditData = statusData;
          break;
        }

        // Wait 5 seconds before next poll
        await new Promise((resolve) => setTimeout(resolve, 5000));
        attempts++;
      }

      if (!auditData) {
        throw new Error('Audit timed out or failed');
      }

      setSeoScanData(auditData);

      // Step 3: Patch the client with new SEO data
      if (auditData) {
        await axios.post(
          `https://dincsdvg9l.execute-api.us-east-1.amazonaws.com/metrics/seo/patch`,
          {
            clientId,
            score: auditData.overallScore,
            passed: auditData.summary?.passed,
            warnings: auditData.summary?.warnings,
            failed: auditData.summary?.failed,
            lastScanDate: auditData.createdAt,
            reportId: auditData.reportId
          },
          { headers: { 'Content-Type': 'application/json' } }
        );
      }
    } catch (error) {
      console.error('Error fetching SEO audit:', error);
    } finally {
      setIsLoadingSeo(false);
    }
  };

  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;

    try {
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        dispatch(setSelectedClient(response.data));
      } else {
        dispatch(setSelectedClient(selectedOption));
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      dispatch(setSelectedClient(selectedOption));
    }

    history.push('/reports');
  };

  const exportToPDF = async () => {
    if (!reportContentRef.current) return;

    try {
      const element = reportContentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20; // 10mm margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10; // 10mm top margin

      // Add image pages
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - 20;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight - 20;
      }

      // Generate filename with client name and date range
      const clientName = selectedClient?.clientName || 'Report';
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${clientName}_Report_${timestamp}.pdf`;

      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      <main
        ref={reportContentRef}
        className="overflow-x-hidden"
        style={{
          padding: isMobileView ? '0.75rem' : '2.5rem',
          paddingBottom: isMobileView ? '1rem' : '1.5rem'
        }}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-4 min-w-0">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground break-words">
                Reports, {selectedClient ? selectedClient.clientName : ''}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Overview of your performance by category
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
            {isSuperAdmin && !isWebviewMode && (
              <div className="hidden lg:block relative w-72">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                <ReactSelect
                  placeholder="Search"
                  onChange={(client) => handleChangeClient(client)}
                  options={getCustomerList}
                  hideSelectedOptions={true}
                  controlShouldRenderValue={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null
                  }}
                  openMenuOnClick={false}
                  openMenuOnFocus={false}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderRadius: '0.75rem',
                      borderWidth: '1px',
                      borderColor: state.isFocused
                        ? 'hsl(var(--primary))'
                        : 'hsl(var(--border))',
                      boxShadow: state.isFocused
                        ? '0 0 0 1px hsl(var(--primary))'
                        : 'none',
                      backgroundColor: '#ffffff',
                      paddingTop: '10px',
                      paddingBottom: '9px',
                      paddingLeft: '2.5rem',
                      fontSize: '1rem',
                      '&:hover': {
                        borderColor: 'hsl(var(--primary))'
                      }
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: 'hsl(var(--muted-foreground))',
                      fontSize: '1rem'
                    }),
                    input: (provided) => ({
                      ...provided,
                      fontSize: '1rem',
                      color: 'hsl(var(--foreground))'
                    }),
                    menu: (provided) => ({
                      ...provided,
                      borderRadius: '0.75rem',
                      overflow: 'hidden',
                      zIndex: 9999,
                      boxShadow:
                        '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 9999
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                      color: state.isFocused ? '#1e293b' : '#64748b',
                      cursor: 'pointer',
                      padding: '12px 16px',
                      fontSize: '1rem'
                    })
                  }}
                />
              </div>
            )}

            <div className="w-full sm:w-[210px]">
              <ReactSelect
                value={
                  DATE_RANGE_OPTIONS.find(
                    (option) => option.value === dateRange
                  ) || DATE_RANGE_OPTIONS[2]
                }
                onChange={(option) =>
                  setDateRange(option?.value || 'last-6-months')
                }
                options={DATE_RANGE_OPTIONS}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dateRangeSelectStyles}
              />
            </div>

            <Button
              onClick={exportToPDF}
              className="h-12 sm:h-14 px-5 sm:px-7 sm:min-w-[204px] text-sm sm:text-base font-semibold rounded-xl gap-2 shadow-md hover:shadow-lg transition-all"
              style={{
                backgroundColor: '#3766a8',
                color: 'white'
              }}>
              <Download className="h-5 w-5" />
              Export to PDF
            </Button>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-8">
          <SectionCard icon={Star} title="Reviews Wins">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 sm:gap-6">
              <Stat
                value={reportsData.reviewsWins.newReviews}
                label="New reviews this period"
                accent="green"
                icon={ArrowUp}
              />
              <Stat
                value={reportsData.reviewsWins.averageRating}
                label="Average star rating"
                accent="primary"
              />
              <Stat
                value={reportsData.reviewsWins.fiveStarReviews}
                label="5-star reviews"
              />
              <Stat
                value={reportsData.reviewsWins.respondedReviews}
                label="Reviews responded to"
              />
              <Stat
                value={reportsData.reviewsWins.responseRate}
                label="Response rate"
                accent="green"
              />
              <Stat
                value={reportsData.reviewsWins.smsRequests}
                label="SMS review requests sent"
                accent="green"
                icon={Send}
              />
            </div>

            <div
              className="mt-6 p-4 sm:p-5 rounded-xl bg-muted/40"
              style={{ borderLeft: '4px solid #3766a8' }}>
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star
                    key={`review-star-${index}`}
                    className="h-4 w-4"
                    style={{ fill: '#fbbf24', color: '#fbbf24' }}
                  />
                ))}
              </div>
              <p className="text-sm sm:text-base italic text-foreground leading-relaxed">
                {reportsData.reviewsWins.highlightedReview}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                - {reportsData.reviewsWins.highlightedAuthor}
              </p>
            </div>
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
            <SectionCard icon={MapPin} title="Google Business Profile Health">
              <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <CircularScore
                    value={reportsData.profileHealth.optimization}
                  />
                  <p className="text-sm text-muted-foreground font-medium">
                    Optimization
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 flex-1 w-full sm:border-l sm:border-border sm:pl-6">
                  <Stat
                    value={reportsData.profileHealth.gbpPosts}
                    label="GBP posts published"
                  />
                  <Stat
                    value={reportsData.profileHealth.photosAdded}
                    label="Photos added"
                    accent="green"
                    icon={ArrowUp}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard icon={Globe} title="Website SEO Scan">
              <div className="relative">
                {reportsData.seoScan.score > 0 &&
                  reportsData.seoScan.lastScan !== '' && (
                    <button
                      onClick={handleSeoRefresh}
                      disabled={isLoadingSeo}
                      className="absolute -top-3 -right-3 h-8 w-8 rounded-full bg-muted border border-border flex items-center justify-center hover:bg-muted/80 transition-colors z-10 disabled:cursor-not-allowed">
                      <RefreshCw
                        className={`h-4 w-4 text-muted-foreground ${
                          isLoadingSeo ? 'animate-spin' : ''
                        }`}
                      />
                    </button>
                  )}
                <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
                  <div className="flex flex-col items-center gap-2 shrink-0">
                    <CircularScore
                      value={reportsData.seoScan.score}
                      suffix=""
                    />
                    <p className="text-sm text-muted-foreground font-medium">
                      SEO Score
                    </p>
                  </div>

                  <div className="flex-1 w-full space-y-3 sm:border-l sm:border-border sm:pl-6">
                    <div className="grid grid-cols-3 gap-4">
                      <Stat
                        value={reportsData.seoScan.passed}
                        label="Passed"
                        accent="green"
                      />
                      <Stat
                        value={reportsData.seoScan.warnings}
                        label="Warnings"
                        accent="yellow"
                      />
                      <Stat
                        value={reportsData.seoScan.failed}
                        label="Failed"
                        accent="red"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Last scan: {reportsData.seoScan.lastScan}
                    </p>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          <SectionCard icon={MapPin} title="Google Business Performance">
            {isLoadingGbpPerformance ? (
              <p className="text-sm text-muted-foreground">
                Loading Google Business performance...
              </p>
            ) : !GBP_PERFORMANCE_ENDPOINT ? (
              <p className="text-sm text-muted-foreground">
                GBP performance endpoint is not configured.
              </p>
            ) : !gbpPerformanceData?.performance ? (
              <p className="text-sm text-muted-foreground">
                No Google Business performance data available for this client.
              </p>
            ) : (
              <>
                <div className="flex gap-4 sm:gap-6 border-b border-border overflow-x-auto">
                  {GBP_PERFORMANCE_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setGbpTab(tab.id)}
                      className={`pb-2.5 text-sm sm:text-base whitespace-nowrap transition-colors ${
                        gbpTab === tab.id
                          ? 'text-primary font-medium border-b-2 border-primary'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}>
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="mt-6">
                  <p className="text-4xl sm:text-5xl font-bold text-foreground">
                    {activeGbpSeries.total}
                  </p>
                  <p className="text-sm sm:text-base text-muted-foreground mt-1 flex items-center gap-1.5">
                    {activeGbpSeries.label}
                    <Info className="h-4 w-4" />
                  </p>
                </div>
                <div className="h-56 sm:h-64 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={activeGbpSeries.data || []}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e2e8f0"
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12, fill: '#64748b' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 12, fill: '#64748b' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0',
                          background: '#ffffff'
                        }}
                      />
                      <Area
                        type="linear"
                        dataKey="value"
                        stroke="#3766a8"
                        strokeWidth={2}
                        fill="rgba(55, 102, 168, 0.1)"
                        dot={{
                          r: 4,
                          fill: '#ffffff',
                          stroke: '#3766a8',
                          strokeWidth: 2
                        }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </SectionCard>

          <div>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-lg sm:text-xl font-semibold">
                How people discovered you
              </h2>
              <Info className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
              <Card style={sectionCardStyle}>
                <div className="p-5 sm:p-7">
                  {isLoadingGbpPerformance ? (
                    <p className="text-sm text-muted-foreground">Loading...</p>
                  ) : (
                    <>
                      <p className="text-3xl sm:text-4xl font-bold text-foreground">
                        {discoveryTotalViews}
                      </p>
                      <p className="text-sm sm:text-base text-muted-foreground mt-1 flex items-center gap-1.5">
                        <Eye className="h-4 w-4" />
                        People viewed your Business Profile
                      </p>
                      <div className="mt-6">
                        <p className="text-sm font-semibold text-foreground">
                          Platform and device breakdown
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Platform and devices that people used to find your
                          profile
                        </p>
                        {discoveryBreakdown.length === 0 ? (
                          <p className="text-sm text-muted-foreground mt-5">
                            No discovery data for this period.
                          </p>
                        ) : (
                          <div className="flex flex-col sm:flex-row items-center gap-5 mt-5">
                            <div className="h-40 w-40 shrink-0">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={discoveryBreakdown}
                                    dataKey="count"
                                    nameKey="label"
                                    innerRadius={45}
                                    outerRadius={70}
                                    paddingAngle={2}
                                    stroke="none">
                                    {discoveryBreakdown.map((entry) => (
                                      <Cell
                                        key={entry.label}
                                        fill={entry.color}
                                      />
                                    ))}
                                  </Pie>
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="space-y-2.5 flex-1">
                              {discoveryBreakdown.map((entry) => (
                                <div
                                  key={entry.label}
                                  className="flex items-center gap-2.5">
                                  <span
                                    className="h-3 w-3 rounded-full shrink-0"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-sm font-semibold text-foreground">
                                    {entry.count} · {entry.pct}%
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    {entry.label}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </Card>

              <Card style={sectionCardStyle}>
                <div className="p-5 sm:p-7">
                  {isLoadingGbpPerformance ? (
                    <p className="text-sm text-muted-foreground">Loading...</p>
                  ) : (
                    <>
                      <p className="text-3xl sm:text-4xl font-bold text-foreground">
                        {searchKeywordsTotal}
                      </p>
                      <p className="text-sm sm:text-base text-muted-foreground mt-1 flex items-center gap-1.5">
                        <Search className="h-4 w-4" />
                        Searches showed your Business Profile in the search
                        results
                      </p>
                      <div className="mt-6">
                        <p className="text-sm font-semibold text-foreground">
                          Searches breakdown
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Search terms that showed your Business Profile in the
                          search results
                          {searchKeywordsRangeLabel
                            ? ` · ${searchKeywordsRangeLabel}`
                            : ''}
                        </p>
                        {searchKeywordTerms.length === 0 ? (
                          <p className="text-sm text-muted-foreground mt-4">
                            No search terms for this period.
                          </p>
                        ) : (
                          <>
                            <div className="mt-4 space-y-2">
                              {visibleKeywords.map((item, index) => (
                                <div
                                  key={`${item.term}-${index}`}
                                  className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <span className="text-sm font-medium text-muted-foreground shrink-0">
                                      {index + 1}.
                                    </span>
                                    <span className="text-sm sm:text-base font-medium truncate">
                                      {item.term}
                                    </span>
                                  </div>
                                  <span className="text-sm sm:text-base font-semibold text-foreground shrink-0">
                                    {item.count}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {searchKeywordTerms.length > visibleSearchTerms && (
                              <div className="flex justify-center mt-5">
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="rounded-full text-primary font-medium"
                                  onClick={() =>
                                    setVisibleSearchTerms((prev) => prev + 5)
                                  }>
                                  See more
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </div>
          </div>

          <div className="hidden">
            <SectionCard icon={TrendingUp} title="Local Ranking Momentum">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 mb-6">
                <Stat
                  value={reportsData.rankingMomentum.keywordsTracked}
                  label="Keywords tracked"
                />
                <Stat
                  value={reportsData.rankingMomentum.rankedTop3}
                  label="Ranked in top 3"
                  accent="primary"
                />
                <Stat
                  value={reportsData.rankingMomentum.improvedPeriod}
                  label="Improved this period"
                  accent="green"
                  icon={ArrowUp}
                />
              </div>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-foreground">
                  Top keywords — before & after
                </p>
                {(reportsData.rankingMomentum.keywords || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No ranking keyword data for this period.
                  </p>
                ) : (
                  reportsData.rankingMomentum.keywords.slice(0, 3).map((kw) => (
                    <div
                      key={kw.keyword}
                      className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-muted/50">
                      <div className="flex items-center gap-2 min-w-0">
                        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm sm:text-base font-medium truncate">
                          {kw.keyword}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                        <span className="text-sm text-muted-foreground line-through">
                          #{kw.before ?? '-'}
                        </span>
                        <ArrowUp className="h-4 w-4 text-emerald-600" />
                        <span className="text-base sm:text-lg font-bold text-emerald-600">
                          #{kw.after ?? '-'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </SectionCard>
          </div>

          <SectionCard icon={Share2} title="Social & Content Activity">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <ImageIcon className="h-5 w-5 text-primary" />
                </div>
                <Stat
                  value={reportsData.socialContent.postsPublished}
                  label="Posts published this period"
                />
              </div>

              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <CalendarIcon className="h-5 w-5 text-primary" />
                </div>
                <Stat
                  value={reportsData.socialContent.upcomingPosts}
                  label="Upcoming scheduled posts"
                />
              </div>
            </div>
          </SectionCard>

          <Card
            style={{
              ...sectionCardStyle,
              backgroundColor: '#0f1116',
              color: 'white'
            }}>
            <div className="p-5 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full flex items-center justify-center bg-white/10">
                  <MessageSquare className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-white/70">
                    Your Account Manager
                  </p>
                  <p className="text-base sm:text-lg font-semibold">
                    {reportsData.accountManager.name}
                  </p>
                  {reportsData.accountManager.email ? (
                    <a
                      href={`mailto:${reportsData.accountManager.email}`}
                      className="text-sm text-white/80 hover:text-white underline">
                      {reportsData.accountManager.email}
                    </a>
                  ) : (
                    <p className="text-sm text-white/70">Email not available</p>
                  )}
                </div>
              </div>

              <Button
                onClick={exportToPDF}
                className="h-12 sm:h-14 px-5 sm:px-6 text-sm sm:text-base font-semibold rounded-xl gap-2 w-full sm:w-auto"
                style={{
                  backgroundColor: 'white',
                  color: '#111827'
                }}>
                <FileText className="h-5 w-5" />
                Export to PDF
              </Button>
            </div>
          </Card>
        </div>

        {isLoadingSummary && (
          <p className="mt-4 text-sm text-muted-foreground">
            Loading report metrics...
          </p>
        )}
        {!isLoadingSummary && isLoadingGbpPerformance && (
          <p className="mt-4 text-sm text-muted-foreground">
            Loading Google Business performance...
          </p>
        )}
      </main>
    </div>
  );
};

export default Reports;
